import React, { useState } from 'react';
import { isValid } from 'date-fns';
import { Card, CardHeader, CardTitle, CardContent, Input } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { LoadingSpinner, ErrorMessage } from '@/components/common';
import { useAuditTrail } from '@/hooks/useAudit';
import type { ChangeType } from '@/types';

interface AuditViewerProps {
  tableName: string;
  recordId: string;
  title?: string;
}

export default function AuditViewer({ tableName, recordId, title = 'Change History' }: AuditViewerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChangeType, setFilterChangeType] = useState<ChangeType | 'ALL'>('ALL');
  const [limit, setLimit] = useState(100);

  const { data: auditTrail, isLoading, error } = useAuditTrail(tableName, recordId, limit);

  // Helper function to format timestamps in the user's local timezone
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    if (!isValid(date)) return 'Invalid date';
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorMessage message="Failed to load audit trail. Please try again." />
        </CardContent>
      </Card>
    );
  }

  // Filter audit trail based on search and change type
  const filteredAuditTrail = auditTrail?.filter((entry) => {
    const matchesSearch = searchTerm === '' || 
      entry.fieldName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.changedByName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.changedByEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.oldValue?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.newValue?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesChangeType = filterChangeType === 'ALL' || entry.changeType === filterChangeType;

    return matchesSearch && matchesChangeType;
  }) || [];

  const getChangeTypeBadge = (changeType: ChangeType) => {
    const colors: Record<ChangeType, string> = {
      INSERT: 'bg-green-100 text-green-800',
      UPDATE: 'bg-outfront-purple-light text-outfront-purple',
      DELETE: 'bg-red-100 text-red-800',
    };
    return (
      <Badge className={colors[changeType]}>
        {changeType}
      </Badge>
    );
  };

  const formatFieldName = (fieldName: string) => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatValue = (value: string | null) => {
    if (value === null) return <span className="text-neutral-400 italic">null</span>;
    if (value === '') return <span className="text-neutral-400 italic">empty</span>;
    
    // Try to format as date if it looks like a date
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        const date = new Date(value);
        if (isValid(date)) {
          // Date-only values (YYYY-MM-DD) → display in UTC to preserve the calendar date
          // Full timestamps (with T or time component) → display in local timezone
          const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
          if (isDateOnly) {
            return date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            });
          }
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
        }
      } catch {
        // Fall through to return original value
      }
    }
    
    return value;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search by field, user, or value..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterChangeType}
              onChange={(e) => setFilterChangeType(e.target.value as ChangeType | 'ALL')}
              className="px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple"
            >
              <option value="ALL">All Changes</option>
              <option value="INSERT">Insert</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
            </select>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple"
            >
              <option value="50">50 entries</option>
              <option value="100">100 entries</option>
              <option value="200">200 entries</option>
              <option value="500">500 entries</option>
            </select>
          </div>
        </div>

        {/* Audit Trail */}
        {filteredAuditTrail.length === 0 ? (
          <div className="text-center py-8 text-neutral-400">
            {searchTerm || filterChangeType !== 'ALL' 
              ? 'No changes match your filters.'
              : 'No change history available.'}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAuditTrail.map((entry) => (
              <div
                key={entry.id}
                className="border border-neutral-200 rounded-lg p-4 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {getChangeTypeBadge(entry.changeType)}
                    <span className="font-medium text-neutral-900">
                      {formatFieldName(entry.fieldName)}
                    </span>
                  </div>
                  <span className="text-sm text-neutral-400">
                    {formatDate(entry.timestamp)}
                  </span>
                </div>

                {entry.changeType === 'UPDATE' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 pl-4 border-l-2 border-neutral-200">
                    <div>
                      <span className="text-xs font-medium text-neutral-400 uppercase">Old Value</span>
                      <div className="mt-1 text-sm text-neutral-600 bg-red-50 px-2 py-1 rounded">
                        {formatValue(entry.oldValue)}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-neutral-400 uppercase">New Value</span>
                      <div className="mt-1 text-sm text-neutral-600 bg-green-50 px-2 py-1 rounded">
                        {formatValue(entry.newValue)}
                      </div>
                    </div>
                  </div>
                )}

                {entry.changeType === 'INSERT' && (
                  <div className="mt-3 pl-4 border-l-2 border-neutral-200">
                    <span className="text-xs font-medium text-neutral-400 uppercase">Value</span>
                    <div className="mt-1 text-sm text-neutral-600 bg-green-50 px-2 py-1 rounded">
                      {formatValue(entry.newValue)}
                    </div>
                  </div>
                )}

                {entry.changeType === 'DELETE' && (
                  <div className="mt-3 pl-4 border-l-2 border-neutral-200">
                    <span className="text-xs font-medium text-neutral-400 uppercase">Deleted Value</span>
                    <div className="mt-1 text-sm text-neutral-600 bg-red-50 px-2 py-1 rounded">
                      {formatValue(entry.oldValue)}
                    </div>
                  </div>
                )}

                <div className="mt-3 flex items-center text-sm text-neutral-600">
                  <span className="font-medium">Changed by:</span>
                  <span className="ml-2">{entry.changedByName}</span>
                  <span className="ml-1 text-neutral-400">({entry.changedByEmail})</span>
                </div>

                {entry.context && Object.keys(entry.context).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-neutral-400 cursor-pointer hover:text-neutral-600">
                      Additional Context
                    </summary>
                    <pre className="mt-2 text-xs bg-neutral-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(entry.context, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        <div className="mt-6 pt-4 border-t border-neutral-200 text-sm text-neutral-600">
          Showing {filteredAuditTrail.length} of {auditTrail?.length || 0} changes
        </div>
      </CardContent>
    </Card>
  );
}
